import { Async } from '../../platform/async/Async'
import { Skeleton } from '../../platform/async/Skeleton'
import { InlineLink } from '../../platform/primitives/InlineLink'
import { Money } from '../../platform/primitives/Money'
import { Panel } from '../../platform/primitives/Panel'
import { useMerchantStats, type CurrencyStats } from './useMerchantStats'

const SKELETON = (
  <div className="txn-stats__skeleton">
    <Skeleton width="50%" height={20} />
    <Skeleton width="100%" height={44} />
  </div>
)

/**
 * "You at {merchant}": this year's spend, visits and average (Q12). Three
 * numbers, no bars. Hidden entirely when the transaction has no merchant.
 */
export function MerchantStats({ merchantId, name, today }: { merchantId: string | null; name: string; today: Date }) {
  const state = useMerchantStats(merchantId, today)
  if (state === null) return null
  return (
    <Panel className="txn-stats">
      <h3>{`You at ${name}`}</h3>
      <Async
        surface="merchant-stats"
        state={state}
        skeleton={SKELETON}
        empty={{ title: `No visits to ${name} yet this year.` }}
      >
        {(stats) => (
          <>
            {stats.byCurrency.map((c) => (
              <Figures key={c.currency} c={c} atLeast={stats.atLeast} labelled={stats.byCurrency.length > 1} />
            ))}
            <InlineLink to={`/merchants/${merchantId}/transactions`} className="txn-stats__open">Open merchant →</InlineLink>
          </>
        )}
      </Async>
    </Panel>
  )
}

function Figures({ c, atLeast, labelled }: { c: CurrencyStats; atLeast: boolean; labelled: boolean }) {
  return (
    <div className="txn-stats__block">
      {labelled && <p className="txn-stats__currency">{c.currency}</p>}
      <dl className="txn-stats__figures">
      <div>
        <dt>This year</dt>
        <dd>
          {atLeast && <span className="txn-stats__atleast">at least </span>}
          <Money cents={c.spentCents} currency={c.currency} />
        </dd>
      </div>
      <div>
        <dt>Visits</dt>
        <dd>
          {atLeast && <span className="txn-stats__atleast">at least </span>}
          {c.visits}
        </dd>
      </div>
      <div>
        <dt>Average</dt>
        <dd>
          <Money cents={c.averageCents} currency={c.currency} />
        </dd>
      </div>
      </dl>
    </div>
  )
}
